import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface TooltipProps {
  /** Text or element that triggers the tooltip */
  children: React.ReactNode;
  /** Tooltip contents */
  content: React.ReactNode;
  /** Extra classes for the floating tooltip container */
  contentClassName?: string;
  /** Override width utility class (default w-72) */
  widthClassName?: string;
  /**
   * Bare trigger: no dotted underline / help cursor and no extra tab stop —
   * for already-interactive triggers like icon buttons (the child supplies its
   * own focusability and cursor). Default keeps the text-trigger styling.
   */
  bare?: boolean;
}

/** Gap between the trigger and the box, and minimum gap to the viewport edge. */
const TRIGGER_GAP = 8;
const VIEWPORT_MARGIN = 8;

/**
 * Simple utility tooltip that appears on hover/focus.
 *
 * The floating box renders in a portal on document.body with fixed,
 * viewport-clamped positioning (below the trigger, flipped above when there's
 * no room): ancestor overflow/stacking contexts can't clip it, and it never
 * widens the document into sideways scrolling on mobile — both real bugs of
 * the old in-place absolute box (product backlog #17).
 */
const Tooltip: React.FC<TooltipProps> = ({
  children,
  content,
  contentClassName = '',
  widthClassName = 'w-72',
  bare = false,
}) => {
  const triggerRef = useRef<HTMLSpanElement>(null);
  const boxRef = useRef<HTMLSpanElement>(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  // Measure after mount (the box renders hidden for one frame), then place.
  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    const trigger = triggerRef.current;
    const box = boxRef.current;
    if (!trigger || !box) return;
    const t = trigger.getBoundingClientRect();
    const b = box.getBoundingClientRect();
    let top = t.bottom + TRIGGER_GAP;
    if (top + b.height > window.innerHeight - VIEWPORT_MARGIN && t.top - TRIGGER_GAP - b.height >= VIEWPORT_MARGIN) {
      top = t.top - TRIGGER_GAP - b.height;
    }
    const left = Math.max(VIEWPORT_MARGIN, Math.min(t.left, window.innerWidth - b.width - VIEWPORT_MARGIN));
    setPos({ top, left });
  }, [open]);

  // A fixed box goes stale the moment the page scrolls or resizes under it.
  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open]);

  return (
    <span
      ref={triggerRef}
      className="relative inline-flex"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
    >
      <span
        {...(bare ? {} : { tabIndex: 0 })}
        className={bare ? 'inline-flex' : 'underline decoration-dotted cursor-help focus:outline-none'}
      >
        {children}
      </span>
      {open &&
        createPortal(
          <span
            ref={boxRef}
            role="tooltip"
            style={{
              position: 'fixed',
              top: pos?.top ?? 0,
              left: pos?.left ?? 0,
              visibility: pos ? 'visible' : 'hidden',
              // Above BottomSheet (2500); below the fullscreen loader (9999).
              zIndex: 3000,
            }}
            className={`${widthClassName} p-3 text-xs bg-gray-900 text-white rounded shadow-lg ${contentClassName}`}
          >
            {content}
          </span>,
          document.body,
        )}
    </span>
  );
};

export default Tooltip;
